import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { Box, Typography } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { SystemBanner } from "../../../components/UIToolkit/molecules/SystemBanner";

/** Bullet keys, in reading order. The suffix after the last dot is also the per-bullet test id. */
const AUDIT_BULLET_KEYS = ["employees", "breaks", "cap"] as const;

type Props = {
	/**
	 * Submittable audit hours in the scored window with no CMS job mapping, so they could not be
	 * scored. `0` means the mapping was checked and is complete — a quieter banner, not a missing
	 * one, because the substitution itself is still worth disclosing.
	 */
	unmappedHours: number;
};

/**
 * DAT-597 — disclosure for a department whose PBJ star prediction was scored from its PBJ Audit
 * hours rather than from the InHouse staffing schedule.
 *
 * It is the mirror of `AuditTab/ActualsFallbackWarning`, which discloses the opposite substitution,
 * and it earns its place for the same reason: the two sources differ in WHO is counted and in how
 * breaks are netted, so a facility comparing this quarter's HPPD against last quarter's — or against
 * its own Audit tab — needs to know which basis it is looking at. Measured on prod, the swing is
 * 0.98× to 1.15× for SNFs and up to 4.6× for departments that are barely scheduled in InHouse.
 *
 * Unlike its Audit-tab counterpart this is `info`, not `warning`, when the mapping is complete: the
 * substitution is an UPGRADE here (payroll punches are the CMS basis; the schedule is the stand-in),
 * so the copy explains rather than cautions. It escalates to `warning` only for the one thing the
 * facility can act on — audit hours with no CMS job mapping, which are dropped from the score.
 *
 * Rendered only on the audit source. A department on the schedule source sees nothing new, because
 * nothing changed for it.
 */
export const AuditHoursSourceBanner: FC<Props> = ({ unmappedHours }) => {
	const { t } = useTranslation();
	const hasUnmapped = unmappedHours > 0;

	return (
		<SystemBanner
			tone={hasUnmapped ? "warning" : "info"}
			icon={hasUnmapped ? <WarningAmberOutlinedIcon /> : <InsightsOutlinedIcon />}
			title={t("pbj_model.prediction.hours_source.audit.title")}
			description={
				<Box component="ul" sx={{ m: 0, pl: 2.25, display: "flex", flexDirection: "column", gap: 0.375 }}>
					{AUDIT_BULLET_KEYS.map((key) => (
						<Typography component="li" key={key} sx={{ fontSize: "inherit", lineHeight: 1.45 }} data-testid={`pbj-model-prediction-hours-source-${key}`}>
							{t(`pbj_model.prediction.hours_source.audit.${key}`)}
						</Typography>
					))}
					{hasUnmapped && (
						<Typography component="li" sx={{ fontSize: "inherit", lineHeight: 1.45, fontWeight: 600 }} data-testid="pbj-model-prediction-hours-source-unmapped">
							{t("pbj_model.prediction.hours_source.audit.unmapped", { hours: unmappedHours })}
						</Typography>
					)}
				</Box>
			}
			descriptionComponent="div"
			dataTestId="pbj-model-prediction-hours-source-banner"
		/>
	);
};
